<template>
  <div id="add-item-to-basket-overlay">
    <div class="modal fade">
      <div class="modal-dialog" role="document">
        <div class="modal-content" v-if="basketItem">
          <!-- MODAL HEADER -->
          <div class="modal-header">
            <div class="modal-title h5">
              {{ $translate("Ceres::Template.singleItemAdded") }}
            </div>
            <span class="text-muted ml-auto"><span class="timer"></span>s</span>
            <button
              type="button"
              class="close ml-0 pl-1"
              data-dismiss="modal"
              aria-label="Close"
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <!-- ./MODAL HEADER -->

          <!-- MODAL BODY -->
          <div class="modal-body">
            <div class="row is-table-row">
              <div class="col-md-4" style="min-height: 80px">
                <img
                  style="max-height: 140px"
                  class="img-fluid mx-auto"
                  v-lazyload-img
                  :data-src="imageUrl"
                  :alt="imageAlternativeText || itemName"
                  :title="itemName"
                />
              </div>
              <div class="col-md-8">
                <p>
                  <strong>{{ itemName }}</strong>
                  <br />
                  <span v-if="countAdditionalBasketItems > 0">{{
                    $translate(
                      "Ceres::Template.basketItemOverlayAdditionalCount",
                      { count: countAdditionalBasketItems }
                    )
                  }}</span>
                </p>

                <p class="small">
                  <strong>
                    <span class="text-muted">{{ basketItem.quantity }} x </span>
                    <span>{{ price | currency }}</span>
                  </strong>
                </p>
                <p
                  class="small"
                  v-for="attribute in variation.attributes"
                  :key="attribute"
                >
                  <strong>{{ attribute.attribute.names.name }}</strong
                  >:
                  <span>{{ attribute.value.names.name }}</span>
                </p>

                <div class="small" v-if="shownOrderProperties.length">
                  <div class="font-weight-bold my-1">
                    {{
                      $translate("Ceres::Template.singleItemAdditionalOptions")
                    }}:
                  </div>
                  <ul class="ml-1 pl-3">
                    <li
                      v-for="property in shownOrderProperties"
                      :key="property.propertyId"
                    >
                      <span class="d-block">
                        <strong :class="{ colon: property.type.length > 0 }">
                          {{ property.name }}
                          ({{
                            $translate("Ceres::Template.singleItemIncludeAbbr")
                          }}
                          {{
                            basketItem.variation.data.properties
                              | propertySurcharge(property.propertyId)
                              | currency
                          }})
                        </strong>
                        <span>
                          <order-property-value
                            :property="property"
                          ></order-property-value>
                        </span>
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          <!-- ./MODAL BODY -->

          <!-- MODAL FOOTER -->
          <div class="modal-footer">
            <a
              :href="urls.basket"
              rel="nofollow"
              class="btn btn-outline-primary btn-medium mr-2"
            >
              <svg
                width="17"
                height="19"
                viewBox="0 0 17 19"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style="margin-right: 5px; width: 16px; height: 16px"
              >
                <path
                  d="M2.80434 7.65586C2.83273 7.30219 2.99329 6.97215 3.25406 6.73153C3.51482 6.49091 3.85666 6.35731 4.21148 6.35742H12.7206C13.0754 6.35731 13.4173 6.49091 13.6781 6.73153C13.9388 6.97215 14.0994 7.30219 14.1278 7.65586L14.6944 14.7127C14.71 14.9069 14.6852 15.1022 14.6216 15.2864C14.558 15.4705 14.4569 15.6395 14.3248 15.7827C14.1927 15.9259 14.0324 16.0401 13.8539 16.1184C13.6755 16.1965 13.4828 16.2369 13.288 16.237H3.6441C3.44928 16.2369 3.25658 16.1965 3.07813 16.1184C2.89969 16.0401 2.73936 15.9259 2.60724 15.7827C2.47513 15.6395 2.37409 15.4705 2.31048 15.2864C2.24688 15.1022 2.22209 14.9069 2.23768 14.7127L2.80434 7.65586Z"
                  stroke="white"
                  stroke-width="1.95171"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                ></path>
                <path
                  d="M11.2898 8.47322V4.94481C11.2898 4.19617 10.9924 3.4782 10.463 2.94883C9.93361 2.41947 9.21564 2.12207 8.46702 2.12207C7.71839 2.12207 7.00042 2.41947 6.47105 2.94883C5.94168 3.4782 5.64429 4.19617 5.64429 4.94481V8.47322"
                  stroke="white"
                  stroke-width="1.95171"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                ></path>
              </svg>
              {{ $translate("Ceres::Template.singleItemBasket") }}
            </a>
            <a
              :href="urls.checkout"
              rel="nofollow"
              class="btn btn-primary btn-medium"
            >
              <i class="fa fa-arrow-right" aria-hidden="true"></i>
              {{ $translate("Ceres::Template.singleItemCheckout") }}
            </a>
          </div>
          <slot name="extendOverlayButtons"></slot>
          <!-- ./MODAL FOOTER -->
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { isNullOrUndefined } from "../../helper/utils";
import { mapState } from "vuex";

const ModalService = require("../../services/ModalService");
const ApiService = require("../../services/ApiService");

export default {
  props: {
    defaultTimeToClose: {
      type: Number,
      default: 15,
    },
  },

  data() {
    return {
      price: 0,
      basketItem: null,
      countAdditionalBasketItems: 0,
    };
  },

  mounted() {
    if (App.config.basket.addItemToBasketConfirm === "overlay") {
      ApiService.listen("AfterBasketItemUpdate", (data) => {
        const updatedBasketItem = data.basketItems[0];

        if (!this.isBasketItemQuantityUpdate) {
          const basketItem =
            this.basketItems.find((item) => item.id === updatedBasketItem.id) ||
            {};

          this.showItem(basketItem);
        }
      });

      ApiService.listen("AfterBasketItemAdd", (data) => {
        this.showItem(data.basketItems[0], data.basketItems.length - 1);
      });
    }
  },

  computed: {
    ...mapState({
      basketItems: (state) => state.basket.items,
      isBasketItemQuantityUpdate: (state) =>
        state.basket.isBasketItemQuantityUpdate,
    }),

    isLastBasketEntrySet() {
      return !isNullOrUndefined(this.basketItem);
    },

    variation() {
      if (this.basketItem) {
        return this.basketItem.variation
          ? this.basketItem.variation.data
          : null;
      }

      return null;
    },

    itemName() {
      if (this.isLastBasketEntrySet) {
        return this.$options.filters.itemName(this.variation);
      }

      return "";
    },

    imageUrl() {
      if (this.isLastBasketEntrySet) {
        const images = this.$options.filters.itemImages(
          this.variation.images,
          "urlPreview"
        );

        return this.$options.filters.itemImage(images);
      }

      return "";
    },

    imageAlternativeText() {
      if (this.isLastBasketEntrySet) {
        const images = this.$options.filters.itemImages(
          this.variation.images,
          "urlPreview"
        );

        return this.$options.filters.itemImageAlternativeText(images);
      }

      return "";
    },

    urls() {
      return {
        basket: App.urls.basket,
        checkout: App.urls.checkout,
      };
    },

    shownOrderProperties() {
      return this.basketItem.basketItemOrderParams.filter((property) => {
        return !!this.variation.properties.find(
          (prop) => prop.propertyId == property.propertyId
        );
      });
    },
  },

  methods: {
    showItem(basketItem, countAdditionalBasketItems) {
      this.basketItem = basketItem;
      this.countAdditionalBasketItems = countAdditionalBasketItems;
      this.price = basketItem.price;

      ModalService.findModal(
        document.getElementById("add-item-to-basket-overlay")
      )
        .setTimeout(this.defaultTimeToClose * 1000)
        .show();
    },
  },
};
</script>
